import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { BrandMark } from "@/components/BrandMark";
import { ApiError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useLocale } from "@/lib/locale";

const TEXT = {
  it: {
    bad: "Email o password non corretti.",
    pending: "Account in attesa di approvazione. Ti avviseremo via email.",
    suspended: "Account sospeso. Contatta VERSUS.",
    register: "Sei un progettista? Richiedi un account",
  },
  en: {
    bad: "Incorrect email or password.",
    pending: "Account awaiting approval. We'll email you when it's ready.",
    suspended: "Account suspended. Please contact VERSUS.",
    register: "Are you an architect? Request an account",
  },
  es: {
    bad: "Email o contraseña incorrectos.",
    pending: "Cuenta pendiente de aprobación. Te avisaremos por email.",
    suspended: "Cuenta suspendida. Contacta con VERSUS.",
    register: "¿Eres arquitecto? Solicita una cuenta",
  },
} as const;

type LoginText = Record<keyof (typeof TEXT)["it"], string>;

function readError(err: unknown, tx: LoginText): string {
  if (!(err instanceof ApiError)) return tx.bad;
  if (err.status === 403) {
    const body = err.message.toLowerCase();
    if (body.includes("suspend")) return tx.suspended;
    if (body.includes("approval")) return tx.pending;
  }
  return tx.bad;
}

export default function LoginPage() {
  const { login } = useAuth();
  const { locale, t } = useLocale();
  const tx = TEXT[locale];
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate("/projects");
    } catch (err) {
      setError(readError(err, tx));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-surface grid min-h-screen place-items-center px-5 py-10">
      <form onSubmit={onSubmit} className="glass-panel-strong w-full max-w-sm p-7">
        <div className="flex flex-col items-center text-center">
          <BrandMark className="h-16 w-16" />
          <div className="brand-wordmark mt-3 text-[0.68rem] text-paper">
            VERSUS
          </div>
          <div className="brand-kicker mt-1">Surface ID</div>
        </div>

        <hr className="hairline my-6" />

        <label className="block">
          <span className="field-label">{t.email}</span>
          <input
            className="field"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        <label className="mt-3 block">
          <span className="field-label">{t.password}</span>
          <input
            className="field"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {error && <p className="mt-3 text-xs text-pink">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full rounded-full bg-magenta px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-paper transition hover:brightness-110 disabled:opacity-60"
        >
          {t.signIn}
        </button>

        <div className="mt-5 border-t border-white/10 pt-4 text-center">
          <Link
            to="/register"
            className="text-[0.68rem] text-pink hover:underline"
          >
            {tx.register}
          </Link>
        </div>
      </form>
    </div>
  );
}
